<template>
  <div class="peak-line-chart">
    <div class="chart-toolbar">
      <b-dropdown
        class="float-right"
        variant="link"
        toggle-class="text-decoration-none"
        no-caret
        right
      >
        <template #button-content>
          <span class="svg-icon svg-icon-lg">
            <inline-svg src="media/svg/icons/General/Menu.svg" />
          </span>
        </template>
        <b-dropdown-item @click="CSV()">CSV</b-dropdown-item>
        <b-dropdown-item @click="saveImg()">PNG</b-dropdown-item>
      </b-dropdown>
    </div>
    <v-chart ref="chartRef" class="chart" :option="option" />
  </div>
</template>

<script>
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";

import "echarts/lib/component/grid";

import Swal from "sweetalert2";
import { DASHBOARD_CUSTOMER_LINE_EXPORT_CAMPAIGN } from "@/core/services/store/campaign.module";

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent
]);

export default {
  name: "CampaignLineChart",
  components: {
    VChart
  },
  provide: {
    [THEME_KEY]: "light"
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isProPlan: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    filterFrom: {
      type: Date,
      default: null
    },
    filterTo: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      option: {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
            shadowStyle: {
              color: "rgba(79, 129, 189, 0.08)"
            }
          },
          backgroundColor: "rgba(255, 255, 255, 0.96)",
          borderColor: "#dbe4f0",
          borderWidth: 1,
          extraCssText:
            "box-shadow: 0 12px 30px rgba(31, 45, 61, 0.12); border-radius: 14px;",
          padding: [12, 14],
          formatter: function (params) {
            let str = params[0].axisValue + "<br/>";
            params.forEach(function (item) {
              str += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${item.color}"></span> ${item.seriesName}: <b>${item.value}</b><br/>`;
            });
            return str;
          },
          textStyle: {
            color: "#334155",
            fontSize: 13
          }
        },
        legend: {
          bottom: 0,
          left: "center",
          orient: "horizontal",
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 18,
          textStyle: {
            color: "#475569",
            fontSize: 13
          }
        },
        grid: {
          left: "7%",
          right: "3%",
          bottom: 72,
          top: 24,
          containLabel: true
        },
        xAxis: [
          {
            type: "category",
            data: [],
            axisTick: {
              alignWithLabel: true
            },
            axisLine: {
              lineStyle: {
                color: "#cbd5e1"
              }
            },
            axisLabel: {
              color: "#64748b",
              fontSize: 13,
              margin: 14
            }
          }
        ],
        yAxis: [
          {
            type: "value",
            min: 0,
            max(value) {
              if (!value || !value.max) {
                return 10;
              }
              return Math.ceil((value.max * 1.1) / 50) * 50;
            },
            name: "จำนวนใบเสร็จ (ใบ)",
            nameLocation: "middle",
            nameGap: 52,
            nameTextStyle: {
              color: "#64748b",
              fontSize: 13,
              padding: [0, 0, 14, 0]
            },
            splitLine: {
              lineStyle: {
                color: "#e2e8f0",
                type: "dashed"
              }
            },
            axisLine: {
              show: false
            },
            axisLabel: {
              color: "#64748b",
              fontSize: 13
            }
          }
        ],
        series: [],
        color: [
          "#64748b", // อาทิตย์
          "#0ea5e9", // จันทร์
          "#f59e0b", // อังคาร
          "#22c55e", // พุธ
          "#8b5cf6", // พฤหัสบดี
          "#dc2626", // ศุกร์
          "#fbbf24" // เสาร์
        ],
        backgroundColor: "transparent"
      }
    };
  },
  mounted() {
    if (this.data) {
      let seriesData = [];
      for (let index = 0; index < this.data.series.length; index++) {
        const element = this.data.series[index];
        seriesData.push({
          name: element.name,
          type: "bar",
          barMaxWidth: 18,
          barGap: "18%",
          itemStyle: {
            borderRadius: 0,
            opacity: 0.92,
            ...(element.itemStyle || {})
          },
          emphasis: {
            focus: "series",
            itemStyle: {
              opacity: 1
            }
          },
          label: {
            show: false
          },
          data: element.data
        });
      }
      this.option.xAxis[0].data = this.data.categories;
      this.option.series = seriesData;
    }
  },
  methods: {
    csvEscapeCell(val) {
      const s = String(val == null ? "" : val);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
    apiCatchMessage(error) {
      if (error && error.message) return error.message;
      if (error && error.response && error.response.data) {
        const d = error.response.data;
        if (typeof d === "string") return d;
        if (d.message) return d.message;
        if (d.error) return d.error;
      }
      return error != null ? String(error) : "Unknown error";
    },
    async CSV() {
      const exportCategories = this.option.xAxis[0].data || [];
      const exportSeries = this.option.series || [];

      if (!exportCategories.length || !exportSeries.length) {
        return;
      }
      let headers = [
        `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
        )}`
      ];

      for (let index = 0; index < exportSeries.length; index++) {
        const element = exportSeries[index];
        headers.push(element.name);
      }

      let contents = [];
      for (let index = 0; index < exportCategories.length; index++) {
        const element = exportCategories[index];
        contents.push([this.csvEscapeCell(element)]);
      }

      for (let index = 0; index < exportSeries.length; index++) {
        const element = exportSeries[index];
        for (let index2 = 0; index2 < element.data.length; index2++) {
          const element2 = element.data[index2];
          contents[index2].push(element2);
        }
      }

      // prepare CSV data
      let csvData = [];
      csvData.push(headers);
      for (let index = 0; index < contents.length; index++) {
        const element = contents[index];
        csvData.push(element);
      }

      let lineArray = [];
      csvData.forEach(function (infoArray, index) {
        let line = infoArray.join(",");
        lineArray.push(index == 0 ? "\uFEFF" + line : line);
      });
      let csvContent = lineArray.join("\n");

      // download stuff
      let blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      let link = document.createElement("a");

      if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        link.setAttribute("href", window.URL.createObjectURL(blob));
        link.setAttribute(
          "download",
          `${this.$t(
            "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.PEAK_LINE_Y"
          )}.csv`
        );
        link.setAttribute("hidden", true);
      } else {
        // it needs to implement server side export
        link.setAttribute("href", "#");
      }
      link.click();
    },
    async exportCsvPro() {
      try {
        const { data } = await this.$store.dispatch(
          DASHBOARD_CUSTOMER_LINE_EXPORT_CAMPAIGN,
          {
            id: this.campaignId,
            params: {
              start_date: this.filterFrom,
              end_date: this.filterTo
            }
          }
        );
        if (data.status === "success" && data.data && data.data.rows) {
          const rows = data.data.rows;
          const headers = [
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CAMPAIGN_LINE_X"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CUSTOMER_LINE_EXPORT_USER_COUNT"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CUSTOMER_LINE_EXPORT_RECEIPT_COUNT"
            ),
            this.$t(
              "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.CUSTOMER_LINE_EXPORT_USER_NAMES"
            )
          ];
          const csvData = [headers];
          for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            const dk = r.date || "";
            const parts = dk.split("-");
            let dateCell = dk;
            if (parts.length === 3) {
              dateCell = `${parts[2]}/${parts[1]}/${parts[0]}`;
            }
            csvData.push([
              this.csvEscapeCell(dateCell),
              this.csvEscapeCell(r.user_count),
              this.csvEscapeCell(r.receipt_count),
              this.csvEscapeCell(r.names)
            ]);
          }
          const lineArray = [];
          csvData.forEach(function (infoArray, index) {
            const line = infoArray.join(",");
            lineArray.push(index === 0 ? "\uFEFF" + line : line);
          });
          const csvContent = lineArray.join("\n");
          const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;"
          });
          const link = document.createElement("a");
          if (link.download !== undefined) {
            link.setAttribute("href", window.URL.createObjectURL(blob));
            link.setAttribute(
              "download",
              `${this.$t(
                "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.PEAK_LINE_Y"
              )}_pro_detail.csv`
            );
            link.setAttribute("hidden", true);
          } else {
            link.setAttribute("href", "#");
          }
          link.click();
        } else if (data.status === "error" && data.error) {
          Swal.fire({
            icon: "error",
            text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
            showConfirmButton: true,
            confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: this.apiCatchMessage(error),
          showConfirmButton: true,
          confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
        });
      }
    },
    saveImg() {
      const chartRef = this.$refs.chartRef;
      const chartInstance =
        chartRef && chartRef.chart ? chartRef.chart : chartRef;
      let imageDataUrl = null;

      if (chartInstance && typeof chartInstance.getDataURL === "function") {
        imageDataUrl = chartInstance.getDataURL({
          type: "png",
          pixelRatio: 2,
          backgroundColor: "#ffffff"
        });
      }

      if (!imageDataUrl) {
        const canvas = this.$el.querySelector("canvas");
        if (canvas) {
          imageDataUrl = canvas.toDataURL("image/png");
        }
      }

      if (imageDataUrl) {
        let tempA = document.createElement("a");
        tempA.download = `${this.$t(
          "PAGE.CAMPAIGN.FORM.CAMPAIGN_RECEIPT.TAB_DASHBOARD.PEAK_LINE_Y"
        )}.png`;
        tempA.href = imageDataUrl;
        document.body.appendChild(tempA);
        tempA.click();
        tempA.remove();
      }
    }
  }
};
</script>

<style scoped>
.peak-line-chart {
  padding: 16px 20px 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 18px;
}

.chart-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.chart {
  height: 600px;
}
</style>

<style>
body {
  margin: 0;
}
</style>
